import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Smartphone, ShieldOff, ShieldCheck } from "lucide-react";

/**
 * Field Access card — admin-only.
 *
 * Lists every `field_sub` user who currently has access to this deal via
 * `/api/deals/{id}/field-access` and provides a one-click Revoke/Restore
 * toggle. Access is derived from vendor linkage (assigned subcontractor,
 * primary subcontractor, or anyone who signed a WO for this deal) with a
 * per-user `blocked_deal_ids` override that we flip here.
 *
 * The card auto-hides for non-admins and when no field_subs qualify — so
 * it stays invisible on deals that never involved a subcontractor.
 */
export default function FieldAccessCard({ dealId, currentUser }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // user_id being toggled

  const isAdmin = currentUser?.role === "admin";

  const load = async () => {
    if (!isAdmin || !dealId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await api.get(`/deals/${dealId}/field-access`);
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [dealId, isAdmin]);

  if (!isAdmin) return null;
  if (!loading && rows.length === 0) return null;

  const toggle = async (row) => {
    setSaving(row.id);
    try {
      const path = row.revoked ? "restore" : "revoke";
      await api.post(`/deals/${dealId}/field-access/${row.id}/${path}`);
      toast.success(row.revoked ? "Access restored" : "Access revoked");
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="field-access-card">
      <div className="flex items-center gap-2 mb-3">
        <Smartphone className="w-4 h-4 text-blue-700" />
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Field App Access</div>
      </div>
      {loading ? (
        <div className="text-sm text-zinc-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <ul className="space-y-2">
          {rows.map((u) => (
            <li
              key={u.id}
              className={`flex items-center justify-between gap-3 border rounded-sm px-3 py-2 ${
                u.revoked ? "bg-zinc-50 border-zinc-200" : "bg-emerald-50/40 border-emerald-200"
              }`}
              data-testid={`field-access-row-${u.id}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold text-zinc-900 truncate">{u.name || u.email}</div>
                <div className="text-[11px] text-zinc-500 truncate">
                  {u.email}
                  {u.vendor_name ? ` · ${u.vendor_name}` : ""}
                </div>
              </div>
              <button
                onClick={() => toggle(u)}
                disabled={saving === u.id}
                className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm border transition-colors ${
                  u.revoked
                    ? "border-zinc-300 text-zinc-700 hover:border-zinc-950 hover:text-zinc-950 bg-white"
                    : "border-red-300 text-red-700 hover:border-red-700 hover:bg-red-50 bg-white"
                } disabled:opacity-50`}
                data-testid={`field-access-toggle-${u.id}`}
              >
                {saving === u.id ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : u.revoked ? (
                  <>
                    <ShieldCheck className="w-3 h-3" /> Restore
                  </>
                ) : (
                  <>
                    <ShieldOff className="w-3 h-3" /> Revoke
                  </>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3 text-[10px] text-zinc-500 leading-snug">
        These field_sub users can see this deal on the mobile Field app.
        Revoke removes it from their list without deactivating their account.
      </div>
    </div>
  );
}
